import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input, OnChanges,
    OnDestroy,
    SimpleChanges
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { BreadCrumb } from './breadcrumb';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from 'environments/environment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ObservableProperty } from '../../../utils/decorators/observable-property.decorator';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { RoutesService } from '../../services/routes/routes.service';
import { deepCopy } from '../../../utils/misc/object-utilities';
import { getTranslation } from '../../../utils/const/translation';
import { KzIconsRegistryService } from '../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsArrowright } from '../../../kz-ui/ui/icon/generated-icons/kzIcons-arrowright.icon';

/*
* As lazy loaded routes are used everywhere in the app, we need to specify when to add a breadcrumb node with route config.
* Config from routes.data:
*  - add 'breadcrumbKey' to declare a breadcrumb node. This key is used to get a translated label
*  - add 'breadcrumb' to declare a label for this node. It could be filled within a Resolver, if this label depend on a dynamic value
*  (eg. a route.param or route.queryParam)
*  - add 'breadcrumbRedirect' to declare a specific breadcrumb link (relative path from parent breadcrumb node)
* */

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BreadcrumbComponent implements OnDestroy, OnChanges {

    public breadcrumbsSubscription: Subscription;
    public breadcrumbs: BreadCrumb[];
    public homeLabel: string;

    @Input() customLabel: string;

    @ObservableProperty({})
    jsonUpdated: SafeHtml;
    public jsonUpdated$: BehaviorSubject<SafeHtml>;

    private readonly json = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: $localize`:@@kz-breadcrumb.home:Home`,
                item: environment.REDIRECT_URL
            }
        ]
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private localize: LocalizeRouterService,
        private sanitizer: DomSanitizer,
        private readonly routesService: RoutesService,
        private readonly kzIconsRegistryService: KzIconsRegistryService,
    ) {
      this.kzIconsRegistryService.registerIcons([kzIconsArrowright]);
        this.breadcrumbsSubscription = this.routesService.eventNavigationEnd.pipe(
            distinctUntilChanged(),
            tap(event => {
                this.homeLabel = $localize`:@@kz-breadcrumb.home:Home`;
                this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
                this.updateSchemaOrg();
            })
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'customLabel' &&  this.customLabel) {
                this.breadcrumbs.push({label: this.customLabel, url: null});
                this.cdr.markForCheck();
            }
        }
    }

    ngOnDestroy(): void {
        this.breadcrumbsSubscription.unsubscribe();
    }

    buildBreadCrumb(route: ActivatedRoute, url: Array<string> = [], breadcrumbs: Array<BreadCrumb> = []): Array<BreadCrumb> {
        if ( (route.routeConfig == null || route.routeConfig.data == null || route.routeConfig.data.breadcrumbKey == null) &&
            route.firstChild == null) {
            return breadcrumbs;
        }
        // If no routeConfig is avalailable then go to next route
        if (route.routeConfig == null || route.routeConfig.data == null || (route.routeConfig.data.breadcrumbKey === undefined && route.routeConfig.data.breadcrumb === undefined)) {
            return this.buildBreadCrumb(route.firstChild, url, breadcrumbs);
        }
        // If no routeConfig is avalailable we are on the root path
        // get data from route.snapshot.data (instead of route.routeConfig.data) to get datas from resolvers
        const translationKey = route.snapshot.data && route.snapshot.data.breadcrumbKey ? route.snapshot.data.breadcrumbKey : null;
        const labelFromRoute = route.snapshot.data && route.snapshot.data.breadcrumb ? route.snapshot.data.breadcrumb : null;
        const label = (labelFromRoute) ? labelFromRoute : (translationKey ? getTranslation(translationKey) : '');
        const path = route.routeConfig
            ? ((route.routeConfig.path[0] === ':')
                ? route.snapshot.params[route.routeConfig.path.substring(1)]
                : route.routeConfig.path)
            : '';
        const pathRedirect = (route.snapshot.data && route.snapshot.data.breadcrumbRedirect)
            ? route.snapshot.data.breadcrumbRedirect : null;
        // In the routeConfig the complete path is not available, so we rebuild it each time. If there's a pathRedirect, it is priority
        const nextUrl = [...((url.length === 0) ? ['/'] : url), ((!pathRedirect) ? path : pathRedirect)];
        const breadcrumb: BreadCrumb = {
            label,
            url: nextUrl,
            translationKey
        };
        const newBreadcrumbs = [...breadcrumbs, breadcrumb];

        if (route.firstChild) {
            // If we are not on our current path yet, there will be more children to look after, to build our breadcumb
            return this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs);
        }
        return newBreadcrumbs;
    }

    updateSchemaOrg() {
        const language = this.localize.parser.currentLang;
        const baseUrl = language === 'fr' ? '' : `/${language}`;
        const jsonUpdated = deepCopy<any>(this.json);
        jsonUpdated.itemListElement[0].item = environment.REDIRECT_URL + baseUrl;
        this.breadcrumbs.map((value, index) => {
            const urlTranslated = (language === 'fr' ? '/' : `/${language}/`) +
                (this.localize.translateRoute(value.url.filter((b) => b !== '/')) as string[]).join('/');

            jsonUpdated.itemListElement.push(
                {
                    '@type': 'ListItem',
                    position: (index + 2),
                    name: value.label,
                    item: environment.REDIRECT_URL + ((environment.REDIRECT_URL.substring(0, 1) === '/' || urlTranslated.charAt(0) === '/')
                        ? '' : '/') + urlTranslated
                }
            );
        });
        this.jsonUpdated = this.getSafeHTML(jsonUpdated);
    }

    getSafeHTML(value: {}): SafeHtml {
        // If value convert to JSON and escape / to prevent script tag in JSON
        const jsonString = value ? JSON.stringify(value, null, 2) : '';
        const html = `<script type="application/ld+json">${jsonString}</script>`;
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    trackLabels(index: number, item: any): string {
        return (!item) ? null : item.label;
    }
}
